import { memo, useCallback, type ReactNode } from 'react';
import type { FieldDraft } from '../builder/schema-draft';

export interface WysiwygFieldShellProps {
  field: FieldDraft;
  selected: boolean;
  defaultNode: ReactNode;
  onSelect: (uiId: string | null) => void;
}

/**
 * Selection wrapper around a single rendered field. Idle and hover use
 * `outline` (not `border`) so layout never shifts — the rendered field looks
 * exactly like the read-only preview at all times. Clicking the field selects
 * it and the side-panel inspector handles every edit (label, type, options,
 * formula, etc.). The element itself is never modified in place.
 */
export const WysiwygFieldShell = memo(function WysiwygFieldShell({
  field,
  selected,
  defaultNode,
  onSelect,
}: WysiwygFieldShellProps) {
  const handleMouseDown = useCallback(() => {
    if (!selected) onSelect(field.uiId);
  }, [selected, onSelect, field.uiId]);

  return (
    <div
      className={'wysiwyg-field-shell' + (selected ? ' wysiwyg-field-shell--selected' : '')}
      onMouseDown={handleMouseDown}
      data-ui-id={field.uiId}
    >
      {defaultNode}
    </div>
  );
});
